/**
 * Cart drawer — owns open/close, and the controls the Figma states introduce.
 *
 * assets/cart.js already owns the AJAX layer and bundles the re-rendered
 * section HTML into every 'cart:change' event; component-cart-purchase-mode.js
 * owns subscribe/one-time switching for both the drawer and the cart page. So
 * this file only adds what is left: the Edit panel disclosure, the quick-adds
 * collapse, quick-add variant chips, the loading skeleton and the undo strip.
 *
 * @extends HTMLElement
 */

import { swapLineVariant } from './component-cart-variant-swap.js';

const QTY_DEBOUNCE_MS = 250;

export class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.fetching = false;
    this.onCartChange = this.onCartChange.bind(this);
    this.onCartError = this.onCartError.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onCaptureClick = this.onCaptureClick.bind(this);
    this.onAddSubmit = this.onAddSubmit.bind(this);
    this.onQtyInput = this.onQtyInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onStripPointerDown = this.onStripPointerDown.bind(this);
    this.onStripPointerMove = this.onStripPointerMove.bind(this);
    this.onStripPointerUp = this.onStripPointerUp.bind(this);
    this.onStripClick = this.onStripClick.bind(this);
    this.onStripDragStart = this.onStripDragStart.bind(this);
    this.addEventListener('click', this.onInnerClick);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartChange);
    document.addEventListener('cart:error', this.onCartError);
    document.addEventListener('click', this.onDocumentClick);
    // Capture, because the protection app binds its handler to its own button:
    // a listener on an ancestor would run too late to stop it.
    document.addEventListener('click', this.onCaptureClick, true);
    document.addEventListener('keydown', this.onKeydown);
    // Capture, so the skeleton is in place before cart.js starts the request.
    document.addEventListener('submit', this.onAddSubmit, true);
    this.addEventListener('change', this.onQtyInput);
    this.addEventListener('pointerdown', this.onStripPointerDown);
    this.addEventListener('pointermove', this.onStripPointerMove);
    this.addEventListener('pointerup', this.onStripPointerUp);
    this.addEventListener('pointercancel', this.onStripPointerUp);
    this.addEventListener('click', this.onStripClick, true);
    this.addEventListener('dragstart', this.onStripDragStart);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('cart:error', this.onCartError);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('click', this.onCaptureClick, true);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('submit', this.onAddSubmit, true);
    this.removeEventListener('change', this.onQtyInput);
    this.removeEventListener('pointerdown', this.onStripPointerDown);
    this.removeEventListener('pointermove', this.onStripPointerMove);
    this.removeEventListener('pointerup', this.onStripPointerUp);
    this.removeEventListener('pointercancel', this.onStripPointerUp);
    this.removeEventListener('click', this.onStripClick, true);
    this.removeEventListener('dragstart', this.onStripDragStart);
    this.qtyTimers?.forEach((timer) => clearTimeout(timer));
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  open() {
    // Restored in close() — without this, closing the dialog strands focus on
    // a control that's still visible but no longer the shopper's actual context.
    this.openedFrom = document.activeElement;
    this.classList.add('cart-open');
    // Focus the close control so the dialog is immediately escapable.
    this.querySelector('.cd-header__close')?.focus({ preventScroll: true });
  }

  close() {
    this.classList.remove('cart-open');
    this.openedFrom?.focus({ preventScroll: true });
    this.openedFrom = undefined;
  }

  /** Focusable controls inside the dialog, in tab order — for the Tab trap below. */
  focusableElements() {
    return Array.from(
      this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null);
  }

  toggle() {
    this.classList.contains('cart-open') ? this.close() : this.open();
  }

  /**
   * The protection app hides the theme's CTA and renders its own, which knows
   * nothing about the blocked state. Without this a cart holding a sold-out
   * line still reaches checkout — the exact thing the Blocked frame prevents.
   */
  onCaptureClick(event) {
    if (!this.hasAttribute('data-blocked')) return;
    if (!event.target.closest('#cart-drawer .cd-footer')) return;

    const control = event.target.closest('button, a');
    if (!control) return;
    if (!/checkout/i.test(`${control.textContent} ${control.name || ''}`)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.querySelector('[data-notice-slot]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  onKeydown(event) {
    if (!this.classList.contains('cart-open')) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.focusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onDocumentClick(event) {
    const bubble = event.target.closest('[data-cart-bubble]');
    if (!bubble) return;
    event.preventDefault();
    this.toggle();
  }

  onInnerClick(event) {
    if (event.target.closest('.drawer-overlay, .cd-header__close')) {
      this.close();
      return;
    }

    // Edit discloses the purchase type and cadence panel for that line.
    const editToggle = event.target.closest('[data-edit-toggle]');
    if (editToggle) {
      event.preventDefault();
      this.togglePanel(editToggle);
      return;
    }

    // Quantity. assets/cart.js delegates `/cart/change` links generically, but
    // its stepper branch only recognises `.cart-quantity`, so a drawer stepper
    // falls through to its remove-link branch: it sends the quantity baked into
    // the href instead of stepping from the input, and two fast clicks both
    // send the same value. Handling it here keeps the accumulate-and-debounce
    // behaviour; stopPropagation prevents cart.js handling it a second time.
    const step = event.target.closest('.cd-qty__step');
    if (step) {
      event.preventDefault();
      event.stopPropagation();
      if (step.getAttribute('aria-disabled') !== 'true') this.stepQuantity(step);
      return;
    }

    const sizeOption = event.target.closest('.cd-size__option');
    if (sizeOption) {
      event.preventDefault();
      if (!sizeOption.disabled && sizeOption.getAttribute('aria-pressed') !== 'true') {
        this.swapVariant(sizeOption);
      }
      return;
    }

    const quickAddsToggle = event.target.closest('[data-quick-adds-toggle]');
    if (quickAddsToggle) {
      event.preventDefault();
      const strip = quickAddsToggle.closest('[data-quick-adds]');
      const collapsed = strip.dataset.collapsed === 'true';
      strip.dataset.collapsed = String(!collapsed);
      quickAddsToggle.setAttribute('aria-expanded', String(collapsed));

      const meta = quickAddsToggle.querySelector('[data-quick-adds-meta]');
      if (meta) meta.textContent = collapsed ? meta.dataset.textExpanded : meta.dataset.textCollapsed;
    }
  }

  onQtyInput(event) {
    const input = event.target.closest('.cd-qty input');
    if (!input) return;
    const line = parseInt(input.closest('.cd-qty')?.dataset.cartLine, 10);
    if (!line) return;
    const max = parseInt(input.getAttribute('max'), 10);
    let quantity = Math.max(0, parseInt(input.value, 10) || 0);
    if (!Number.isNaN(max)) quantity = Math.min(quantity, max);
    input.value = quantity;
    this.queueQuantity(line, quantity, input);
  }

  stepQuantity(step) {
    const stepper = step.closest('.cd-qty');
    const input = stepper?.querySelector('input');
    const line = parseInt(stepper?.dataset.cartLine, 10);
    if (!input || !line) return;

    const max = parseInt(input.getAttribute('max'), 10);
    const direction = step === stepper.querySelector('.cd-qty__step:last-of-type') ? 1 : -1;
    let next = Math.max(0, (parseInt(input.value, 10) || 0) + direction);
    if (!Number.isNaN(max)) next = Math.min(next, max);
    input.value = next;
    this.queueQuantity(line, next, step);
  }

  /** Collapses rapid clicks into one request carrying the final quantity. */
  queueQuantity(line, quantity, source) {
    this.qtyTimers = this.qtyTimers || new Map();
    clearTimeout(this.qtyTimers.get(line));
    source.closest('.cd-line')?.setAttribute('aria-busy', 'true');
    this.qtyTimers.set(
      line,
      setTimeout(() => {
        this.qtyTimers.delete(line);
        window.Cart?.change({ line, quantity }, source).catch(() => {
          source.closest('.cd-line')?.setAttribute('aria-busy', 'false');
        });
      }, QTY_DEBOUNCE_MS),
    );
  }

  /**
   * The size-swap panel (Cart_Size_Swap tag). The add-then-remove sequence and
   * its retries live in component-cart-variant-swap.js, shared with the cart page.
   */
  async swapVariant(option) {
    const group = option.closest('[data-cart-line]');
    const line = parseInt(group?.dataset.cartLine, 10);
    const currentLineItem = window.Cart?.state?.items?.[line - 1];
    if (!line || !currentLineItem) return;

    const oldKey = currentLineItem.key;
    const lineRoot = option.closest('.cd-line');
    lineRoot?.setAttribute('aria-busy', 'true');

    try {
      const removed = await swapLineVariant({
        oldKey,
        variantId: option.dataset.variantId,
        quantity: currentLineItem.quantity,
      });
      // The add succeeded (we're past it) but the old line survived every retry —
      // the shopper is left holding both sizes, and refresh() alone won't tell them why.
      if (removed === false) this.insertErrorNotice(this.dataset.i18nSwapError || '');
    } catch {
      // Swallow — refresh() below re-syncs the drawer from the server either way.
    } finally {
      await window.Cart?.refresh();
      lineRoot?.setAttribute('aria-busy', 'false');
    }
  }

  togglePanel(trigger) {
    const line = trigger.closest('.cd-line');
    const panel = line?.querySelector('.cd-panel');
    if (!panel) return;

    const willOpen = panel.hidden;
    panel.hidden = !willOpen;
    // The nudge carries a second trigger for the same panel, so update both.
    line.querySelectorAll('[data-edit-toggle][aria-expanded]').forEach((el) => {
      el.setAttribute('aria-expanded', String(willOpen));
    });
  }

  /**
   * An add-to-cart submit anywhere except the cart page is the moment the
   * Loading frame describes: the drawer is about to open with content it does
   * not have yet. On the cart page the page itself re-renders the new line.
   */
  onAddSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!(form.getAttribute('action') || '').includes('/cart/add')) return;
    if (this.onCartPage()) return;

    this.open();
    this.showSkeleton(CartDrawer.expectedLineCount(new FormData(form)));
  }

  /** The cart template renders <cart-page>. Adds there must not open this drawer. */
  onCartPage() {
    return Boolean(document.querySelector('cart-page'));
  }

  /**
   * Mouse drag for the quick-adds strip. Its scrollbar is hidden, so a mouse
   * has no other way to move it. Touch keeps the browser's own pan. A drag
   * only starts after a few pixels so a click on Add or a size chip still fires.
   * @param {PointerEvent} event
   */
  onStripPointerDown(event) {
    const strip = event.target.closest('.cd-quickadds__strip');
    if (!strip || event.pointerType !== 'mouse' || event.button !== 0) return;

    this.stripDrag = {
      strip,
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: strip.scrollLeft,
      moved: false,
    };
  }

  /**
   * @param {PointerEvent} event
   */
  onStripPointerMove(event) {
    const drag = this.stripDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const distance = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(distance) < 6) return;

    if (!drag.moved) {
      drag.moved = true;
      drag.strip.classList.add('is-dragging');
      drag.strip.setPointerCapture(event.pointerId);
    }

    drag.strip.scrollLeft = drag.scrollLeft - distance;
    event.preventDefault();
  }

  /**
   * @param {PointerEvent} event
   */
  onStripPointerUp(event) {
    const drag = this.stripDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this.stripDrag = undefined;
    this.stripDragMoved = drag.moved;
    drag.strip.classList.remove('is-dragging');
    if (drag.strip.hasPointerCapture(event.pointerId)) {
      drag.strip.releasePointerCapture(event.pointerId);
    }
  }

  /**
   * A drag ends with a click on whatever card was under the pointer. Swallow
   * that click so it does not add the product.
   * @param {MouseEvent} event
   */
  onStripClick(event) {
    if (!this.stripDragMoved) return;
    if (!event.target.closest('.cd-quickadds__strip')) return;

    this.stripDragMoved = false;
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * @param {DragEvent} event
   */
  onStripDragStart(event) {
    if (event.target.closest('.cd-quickadds__strip')) event.preventDefault();
  }

  clearSkeleton() {
    const body = this.querySelector('[data-drawer-body]');
    if (!body || this.loadingSnapshot === undefined) return;
    body.innerHTML = this.loadingSnapshot;
    this.loadingSnapshot = undefined;
    body.removeAttribute('aria-busy');
  }

  /**
   * The Loading frame describes the moment an add is in flight. Replaces
   * whatever [data-drawer-body] currently shows — empty state or real
   * lines — so neither is visible next to placeholders; clearSkeleton()
   * restores the snapshot if the add fails.
   */
  showSkeleton(count = 1) {
    const body = this.querySelector('[data-drawer-body]');
    if (!body || this.loadingSnapshot !== undefined) return;

    this.loadingSnapshot = body.innerHTML;
    const markup = `
      <div class="cd-skeleton" aria-hidden="true">
        <span class="cd-skeleton__thumb"></span>
        <span class="cd-skeleton__body">
          <span class="cd-skeleton__line cd-skeleton__line--mid"></span>
          <span class="cd-skeleton__line cd-skeleton__line--short"></span>
          <span class="cd-skeleton__line cd-skeleton__line--short"></span>
        </span>
      </div>`;
    body.innerHTML = markup.repeat(Math.max(count, 1));
    body.setAttribute('aria-busy', 'true');
  }

  /**
   * How many line items the cart is expected to hold once this submit
   * resolves — existing lines plus any genuinely new variant ids in the
   * submitted form. Re-adding a variant already in the cart bumps its
   * quantity rather than adding a line, so it must not double-count.
   */
  static expectedLineCount(formData) {
    const currentIds = new Set((window.Cart?.state?.items || []).map((item) => String(item.variant_id)));
    const incomingIds = new Set();

    const singleId = formData.get('id');
    if (singleId) incomingIds.add(String(singleId));

    for (const [key, value] of formData.entries()) {
      if (/^items\[\d+\]\[id\]$/.test(key)) incomingIds.add(String(value));
    }

    return new Set([...currentIds, ...incomingIds]).size || 1;
  }

  onCartError(event) {
    this.clearSkeleton();
    const message = event.detail?.error?.description;
    if (message) this.insertErrorNotice(message);

    // A rejected mutation leaves the rendered lines out of step with the cart.
    // Undo, for instance, re-adds an item under a NEW line key, so the old key
    // still in the markup stops resolving and every later action on that line
    // fails with "no valid id or line parameter". Re-sync rather than leave
    // stale controls on screen; assets/cart.js does the same after a failed add.
    window.Cart?.refresh?.();
  }

  /** Server- and merchant-supplied text, so it goes through textContent, never innerHTML. */
  insertErrorNotice(message) {
    const slot = this.querySelector('[data-notice-slot]');
    if (!slot) return;

    const notice = document.createElement('p');
    notice.className = 'cd-notice cd-notice--error';
    notice.setAttribute('role', 'alert');
    const body = document.createElement('span');
    body.textContent = message;
    notice.appendChild(body);
    slot.prepend(notice);
  }

  onCartChange(event) {
    const html = event.detail.sections?.[this.sectionId];
    if (html) {
      this.render(html, event.detail);
    } else {
      this.fetchAndRender(event.detail);
    }
    if (event.detail.action === 'add' && event.detail.source !== 'gift-threshold' && !this.onCartPage()) {
      this.open();
    }

    // Deliberately outside render(): a mutation that comes back without section
    // HTML still removed the line, and the shopper still needs the way back.
    // Gift auto-remove is silent; "No thanks" also skips undo so a restore
    // cannot come back without the _gift_threshold property.
    if (event.detail.action === 'change' && event.detail.payload?.quantity === 0 && event.detail.source !== 'gift-threshold') {
      const removedLine = CartDrawer.lineFromPayload(event.detail.previousCart?.items, event.detail.payload);
      const giftFlag = removedLine?.properties?._gift_threshold;
      if (giftFlag !== 'true' && giftFlag !== true) {
        this.showUndo(event.detail.previousCart, event.detail.payload);
      }
    }
  }

  /** Fallback for a mutation whose response omitted this section's HTML — e.g.
   * the gift-threshold add-to-cart flow, which posts without requesting sections. */
  async fetchAndRender(detail) {
    if (this.fetching || !this.sectionId) return;
    this.fetching = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}?section_id=${encodeURIComponent(this.sectionId)}`, {
        cache: 'no-store',
      });
      if (!response.ok) return;
      const html = await response.text();
      this.render(html, detail);
    } catch {
      /* Keep the last-rendered drawer if the fallback fetch fails. */
    } finally {
      this.fetching = false;
    }
  }

  /**
   * Resolve which line a /cart/change payload referred to.
   *
   * The Cart AJAX API accepts either shape and the theme produces both:
   * `line_item.url_to_remove` targets the line **key** (`id`), while the
   * quantity steppers target the 1-based **index** (`line`). Assuming one
   * shape silently breaks the other.
   */
  static lineFromPayload(items, payload) {
    if (!items || !payload) return null;
    if (payload.id) return items.find((item) => item.key === payload.id) || null;
    if (payload.line) return items[payload.line - 1] || null;
    return null;
  }

  render(html, detail) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('#cart-drawer .drawer__wrapper');
    const current = this.querySelector('.drawer__wrapper');
    if (!next || !current) return;

    this.loadingSnapshot = undefined;

    // Keep the shopper's place in the list unless they just added something.
    const scrollTop = current.querySelector('[data-drawer-body]')?.scrollTop || 0;
    const focusTarget = CartDrawer.captureFocusTarget(current);
    current.innerHTML = next.innerHTML;
    CartDrawer.restoreFocusTarget(current, focusTarget);

    const body = current.querySelector('[data-drawer-body]');
    if (body) body.scrollTop = detail.action === 'add' ? 0 : scrollTop;
  }

  /**
   * Every re-render replaces the whole line-item markup, which drops focus to
   * document.body if the shopper had a quantity/edit/remove control focused —
   * the Tab trap doesn't redirect focus because the old node is simply gone,
   * not outside the drawer. Record which line and which kind of control had
   * focus so render() can put focus back on its replacement.
   * @param {Element} current
   * @returns {{line: string, selector: string} | null}
   */
  static captureFocusTarget(current) {
    const active = document.activeElement;
    if (!active || !current.contains(active)) return null;

    const lineEl = active.closest('[data-cart-line]');
    if (!lineEl) return null;

    const line = lineEl.dataset.cartLine;
    if (active.matches('input[name="updates[]"]')) {
      return { line, selector: 'input[name="updates[]"]' };
    }
    if (active.matches('.cd-qty__step')) {
      const isLast = active === active.parentElement.querySelector('.cd-qty__step:last-of-type');
      return { line, selector: isLast ? '.cd-qty__step:last-of-type' : '.cd-qty__step:first-of-type' };
    }
    if (active.matches('.cd-line__edit, .cd-line__remove, [data-gift-swap]')) {
      const selector = [...active.classList].map((name) => `.${name}`).join('') || active.tagName.toLowerCase();
      return { line, selector };
    }
    return null;
  }

  /**
   * @param {Element} current
   * @param {{line: string, selector: string} | null} target
   */
  static restoreFocusTarget(current, target) {
    if (!target) return;
    const lineEl = current.querySelector(`[data-cart-line="${target.line}"]`);
    lineEl?.querySelector(target.selector)?.focus();
  }

  /** Removals are recoverable — Figma "Cart Drawer / Undo Strip" (4437:5917). */
  showUndo(previousCart, payload) {
    const body = this.querySelector('[data-drawer-body]');
    if (!body || !previousCart) return;

    const removed = CartDrawer.lineFromPayload(previousCart.items, payload);
    if (!removed) return;

    const strip = document.createElement('div');
    strip.className = 'cd-undo';
    strip.setAttribute('role', 'status');
    strip.innerHTML = `<span></span><button type="button" class="cd-undo__action"></button>`;
    strip.firstElementChild.textContent = removed.product_title;
    strip.lastElementChild.textContent = this.dataset.i18nUndo || '';
    strip.lastElementChild.addEventListener('click', () => {
      const data = new FormData();
      data.append('id', removed.variant_id);
      data.append('quantity', removed.quantity);
      if (removed.selling_plan_allocation?.selling_plan?.id) {
        data.append('selling_plan', removed.selling_plan_allocation.selling_plan.id);
      }
      for (const [key, value] of Object.entries(removed.properties || {})) {
        data.append(`properties[${key}]`, value);
      }
      window.Cart?.add(data, strip);
    });

    body.prepend(strip);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/* ---------------------------------------------------------------------------
   Quick-add card — the chips retarget the card's hidden variant input
--------------------------------------------------------------------------- */

if (!customElements.get('cart-quick-add')) {
  customElements.define(
    'cart-quick-add',
    class CartQuickAdd extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        const option = event.target.closest('.cd-qa-card__option');
        if (!option || option.disabled || option.dataset.available === 'false') return;

        for (const chip of this.querySelectorAll('.cd-qa-card__option')) {
          chip.setAttribute('aria-pressed', String(chip === option));
        }

        const input = this.querySelector('[data-qa-variant]');
        if (input) input.value = option.dataset.variantId;

        const price = this.querySelector('[data-qa-price]');
        if (price && option.dataset.variantPrice) price.textContent = option.dataset.variantPrice;

        this.syncAddButton(option);
      }

      /**
       * The button is rendered for the variant that was selected on the server.
       * Picking another size has to retarget it: a size already in the cart
       * stays "In cart", a sold-out size stays "Sold out", anything else is Add.
       * @param {HTMLElement} option
       */
      syncAddButton(option) {
        const button = this.querySelector('.cd-qa-card__add');
        const text = button?.querySelector('[data-atc-text]');
        if (!button || !text) return;

        const inCart = option.dataset.inCart === 'true';
        const available = option.dataset.available !== 'false';

        if (inCart) {
          button.disabled = true;
          text.textContent = button.dataset.labelInCart || text.textContent;
          return;
        }

        if (!available) {
          button.disabled = true;
          text.textContent = button.dataset.labelSoldOut || text.textContent;
          return;
        }

        button.disabled = false;
        text.textContent = button.dataset.labelAdd || text.textContent;
      }
    },
  );
}
